import { useState, useEffect } from 'react';
import { apiFetch } from '~/lib/api';

interface Member {
  _id: string;
  email: string;
  name?: string;
  role: string;
  status: string;
  created_at: string;
}

export default function TbitInvoicesMembersPage() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviting, setInviting] = useState(false);

  const loadMembers = async () => {
    try {
      const [membersRes, sessionRes] = await Promise.all([
        apiFetch('/tbit-invoices/api/members'),
        apiFetch('/tbit-invoices/api/session'),
      ]);

      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.members || []);
      }
      if (sessionRes.ok) {
        const session = await sessionRes.json();
        setIsOwner(session.role === 'OWNER');
      }
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    setInviteError('');

    try {
      const res = await apiFetch('/tbit-invoices/api/members/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || `Error al invitar (${res.status})`);
      }

      setInviteEmail('');
      setShowInvite(false);
      await loadMembers();
    } catch (err: any) {
      setInviteError(err.message || 'Error al enviar la invitación');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Equipo</h1>
        <div className="card bg-base-100 shadow animate-pulse">
          <div className="card-body space-y-3">
            {[1, 2].map(i => <div key={i} className="h-4 bg-base-300 rounded w-64" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Equipo</h1>
        {isOwner && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowInvite(!showInvite)}
          >
            {showInvite ? 'Cancelar' : 'Invitar miembro'}
          </button>
        )}
      </div>

      {showInvite && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Invitar colaborador</h2>
            <form onSubmit={handleInvite} className="flex gap-2">
              <input
                type="email"
                className="input input-bordered flex-1"
                placeholder="colaborador@empresa.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={inviting}
              >
                {inviting ? <span className="loading loading-spinner loading-sm" /> : 'Enviar'}
              </button>
            </form>
            {inviteError && (
              <p className="text-error text-sm mt-1">{inviteError}</p>
            )}
            <p className="text-xs text-base-content/50 mt-1">
              Los colaboradores pueden ver el panel y los registros de facturas, pero no pueden cambiar configuraciones ni subir facturas.
            </p>
          </div>
        </div>
      )}

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          {members.length === 0 ? (
            <p className="text-base-content/50">Aún no hay miembros del equipo.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Correo</th>
                    <th>Nombre</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Ingreso</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m._id}>
                      <td className="text-sm">{m.email}</td>
                      <td className="text-sm text-base-content/60">{m.name || '-'}</td>
                      <td>
                        <span className={`badge badge-sm ${m.role === 'OWNER' ? 'badge-primary' : 'badge-ghost'}`}>
                          {m.role === 'OWNER' ? 'Propietario' : 'Colaborador'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-sm ${m.status === 'ACTIVE' ? 'badge-success' : 'badge-warning'}`}>
                          {m.status === 'ACTIVE' ? 'Activo' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="text-xs text-base-content/50">
                        {new Date(m.created_at).toLocaleDateString('es-CO')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
